import { useState, useEffect, useRef } from "react";
import { BsRobot, BsSend, BsDownload, BsTrash } from "react-icons/bs";
import "./AIChatBot.scss";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  useSendAIMessageMutation,
  useGetAIHistoryQuery,
  useDeleteAIHistoryMutation,
} from "../store/api/aiSlice";
import { toast } from "react-toastify";

// ── Inline markdown: **bold** ────────────────────────────
const applyInline = (str) =>
  str
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        part
      ),
    );

// ── Full markdown renderer (no external lib) ─────────────
const renderMarkdown = (text) => {
  if (!text) return null;
  // Handle both actual newlines and literal \n escape sequences
  const lines = text.replace(/\\n/g, "\n").split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // skip empty lines
    if (!line.trim()) {
      i++;
      continue;
    }

    // markdown table
    if (line.trim().startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }
      if (tableLines.length >= 2) {
        const headers = tableLines[0]
          .split("|")
          .map((h) => h.trim())
          .filter(Boolean);
        const rows = tableLines.slice(2).map((r) =>
          r
            .split("|")
            .map((c) => c.trim())
            .filter(Boolean),
        );
        elements.push(
          <div key={`t${elements.length}`} className="mdTable">
            <table>
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi}>{applyInline(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci}>{applyInline(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
      }
      continue;
    }

    // numbered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const match = lines[i].match(/^(\d+)\.\s/);
        const num = match ? match[1] : "";
        items.push(
          <li key={i} style={{ listStyle: "none", display: "flex", gap: 6, marginBottom: 4 }}>
            <span style={{ minWidth: 18, fontWeight: 600, flexShrink: 0 }}>{num}.</span>
            <span>{applyInline(lines[i].replace(/^\d+\.\s/, ""))}</span>
          </li>,
        );
        i++;
      }
      elements.push(
        <ol key={`ol${elements.length}`} style={{ listStyle: "none", padding: 0, margin: "4px 0" }}>
          {items}
        </ol>,
      );
      continue;
    }

    // bullet list
    if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(
          <li key={i} style={{ listStyle: "none", display: "flex", gap: 6, marginBottom: 4 }}>
            <span style={{ minWidth: 10, flexShrink: 0 }}>•</span>
            <span>{applyInline(lines[i].replace(/^[-*]\s/, ""))}</span>
          </li>,
        );
        i++;
      }
      elements.push(
        <ul key={`ul${elements.length}`} style={{ listStyle: "none", padding: 0, margin: "4px 0" }}>
          {items}
        </ul>,
      );
      continue;
    }

    // heading
    if (/^#{1,3}\s/.test(line)) {
      elements.push(
        <p key={i} className="mdHeading">
          <strong>{applyInline(line.replace(/^#{1,3}\s/, ""))}</strong>
        </p>,
      );
      i++;
      continue;
    }

    // normal paragraph
    elements.push(<p key={i}>{applyInline(line)}</p>);
    i++;
  }

  return elements;
};

const AIChatBot = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);

  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } =
    useGetAIHistoryQuery(30);

  const [sendAIMessage, { isLoading: isSending }] = useSendAIMessageMutation();
  const [deleteAIHistory, { isLoading: isDeleting }] =
    useDeleteAIHistoryMutation();

  // Load history once on mount — raw format: { id, message, response, created_at }
  useEffect(() => {
    if (historyData?.data?.history && !historyLoaded) {
      const loaded = historyData.data.history.flatMap((row) => [
        {
          id: `h-user-${row.id}`,
          text: row.message,
          sender: "user",
          timestamp: row.created_at,
        },
        {
          id: `h-ai-${row.id}`,
          text: row.response,
          sender: "ai",
          timestamp: row.created_at,
        },
      ]);
      setMessages(loaded);
      setHistoryLoaded(true);
    }
  }, [historyData, historyLoaded]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Send Message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMsg = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    const sentText = inputMessage;
    setInputMessage("");

    try {
      const result = await sendAIMessage({
        message: sentText,
        conversationHistory: messages
          .filter((m) => m.sender === "user" || m.sender === "ai")
          .slice(-10)
          .map((m) => ({
            role: m.sender === "user" ? "user" : "assistant",
            content: m.text,
          })),
      }).unwrap();

      const aiMsg = {
        id: Date.now() + 1,
        text: result?.data?.response || "Sorry, I could not get a response.",
        sender: "ai",
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, aiMsg]);
    } catch {
      const errMsg = {
        id: Date.now() + 1,
        text: "AI service is temporarily unavailable. Please try again.",
        sender: "ai",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errMsg]);
    }
  };

  // Export Chat as .txt file
  const exportChat = () => {
    if (messages.length === 0) return;

    const lines = messages.map((msg) => {
      const time = msg.timestamp
        ? moment(msg.timestamp).local().format("DD MMM YYYY, hh:mm A")
        : "";
      const sender = msg.sender === "user" ? "You" : "ASAK AI";
      return `[${time}] ${sender}:\n${msg.text}`;
    });

    const blob = new Blob([lines.join("\n\n")], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asak-chat-${moment().format("YYYY-MM-DD")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Delete all chat history
  const handleDeleteChat = async () => {
    try {
      await deleteAIHistory().unwrap();
      setMessages([]);
      setHistoryLoaded(true); // keep true so useEffect doesn't reload old cache
      await refetchHistory(); // fetch fresh empty history from server
    } catch {
      toast.error(t("delete_chat_history_failed"));
    }
  };

  const isLoading = historyLoading || isSending;

  return (
    <>
      <div className="aiChatBot">
        {/* Header */}
        <div className="chatHeader pe-5 pb-3">
          <div className="chatHeader-right">
            <button
              className="iconBtn"
              onClick={exportChat}
              disabled={!messages.length}
              title="Export chat"
            >
              <BsDownload />
            </button>
            <button
              className="iconBtn deleteBtn"
              onClick={handleDeleteChat}
              disabled={!messages.length || isDeleting}
              title="Delete chat history"
            >
              <BsTrash />
            </button>
          </div>
        </div>

        {/* Chat Messages */}
        <main className="chatBody">
          {historyLoading ? (
            <div className="welcomeScreen">
              <BsRobot className="welcomeIcon" />
              <p>Loading chat history...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="welcomeScreen">
              <BsRobot className="welcomeIcon" />
              <h3>{t("welcome-aeonian")}</h3>
              <p>
                {t("help")} {t("dashboard")}, {t("vendors")}, {t("tracking")},
                {t("drivers")}, {t("reservations")}, {t("vehicles")},{t("and")}{" "}
                {t("more")}.
              </p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`messageRow ${
                  msg.sender === "user" ? "userMsg" : "aiMsg"
                }`}
              >
                <div className="msgAvatar">
                  {msg.sender === "user" ? "" : <BsRobot />}
                </div>
                <div className="msgContent">
                  <div className="msgText">
                    {msg.sender === "ai" ? renderMarkdown(msg.text) : msg.text}
                  </div>
                  {msg.timestamp && (
                    <span className="msgTime">
                      {moment(msg.timestamp).local().format("hh:mm A")}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}

          {isSending && (
            <div className="messageRow aiMsg">
              <div className="msgAvatar">
                <BsRobot />
              </div>
              <div className="msgContent">
                <div className="typingDots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </main>

        {/* Footer Input */}
        <footer className="chatFooter">
          <form onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder={t("ai-textarea")}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              disabled={isLoading}
            />
            <button type="submit" disabled={!inputMessage.trim() || isLoading}>
              <BsSend />
            </button>
          </form>
        </footer>
      </div>
    </>
  );
};

export default AIChatBot;
